"use client"

import React, { useEffect, useRef, useState } from 'react'
import { cn } from '../lib/cn'
import { CheckIcon, FlagIcon, ArrowUpIcon, ArrowDownIcon } from './Icons'

interface FinancialPulseProps {
  className?: string
  spendingStatusIcon?: 'check' | 'arrow-up' | 'arrow-down' | 'none'
  goalsStatusIcon?: 'flag' | 'check' | 'none'
  liquidityBufferContent?: React.ReactNode
  cards: {
    spendingLabel: string
    spendingStatus: string
    savingsRateLabel: string
    savingsRateValue: string
    liquidityBufferLabel: string
    liquidityBufferValue: string
    goalsLabel: string
    goalsStatus: string
  }
}

function GlassCard({ children, className, style }: { children: React.ReactNode, className?: string, style?: React.CSSProperties }) {
  return (
    <div
      className={cn(
        // Subtle glassmorphism card similar to provided SVG
        'rounded-xl bg-[rgba(250,249,245,0.10)] backdrop-blur-md shadow-[0_4px_8px_rgba(0,0,0,0.10)] ring-1 ring-black/5 px-3 py-2 sm:px-4 sm:py-3 text-left',
        'text-[12px] sm:text-sm text-night',
        className
      )}
      style={style}
    >
      {children}
    </div>
  )
}

export function FinancialPulse({ className, cards, spendingStatusIcon = 'check', goalsStatusIcon = 'flag', liquidityBufferContent }: FinancialPulseProps) {
  const [isVisible, setIsVisible] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect() // Only trigger once
        }
      },
      { threshold: 0.3 } // Trigger when 30% visible
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <>
      <style jsx>{`
        /* CSS transitions handle animations now */
      `}</style>
      <div 
        ref={containerRef}
        className={cn(
          'relative w-full h-[340px] sm:h-[380px] md:h-[420px] lg:h-[480px]',
          className
        )}
      >
      {/* Thin upward-trending baseline */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 841 375" preserveAspectRatio="none" aria-hidden>
        <line x1="16" y1="330" x2="825" y2="50" stroke="#D1D5DB" strokeOpacity="0.7" strokeWidth="1" strokeLinecap="round" />
      </svg>

      {/* Curved gradient line matching provided path */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 841 375" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" aria-hidden>
        <path 
          d="M29.5486 369.503C29.5486 369.503 64.6891 272.605 118.503 255.893C168.529 240.356 198.452 306.136 248.256 289.9C300.658 272.817 282.129 197.025 333.697 177.568C386.226 157.749 421.23 226.005 474.282 207.633C528.706 188.787 513.746 115.273 566.75 92.744C624.914 68.0219 669.68 142.393 726.952 115.669C776.343 92.6229 810.637 3.97666 810.637 3.97666" 
          stroke="url(#paint0_linear_1143_3256)" 
          strokeWidth="6.23141"
          strokeDasharray="1000"
          strokeDashoffset={isVisible ? "0" : "1000"}
          style={{
            transition: 'stroke-dashoffset 3s ease-in-out'
          }}
        />
        <defs>
          <linearGradient id="paint0_linear_1143_3256" x1="16.5223" y1="333.714" x2="823.718" y2="39.9184" gradientUnits="userSpaceOnUse">
            <stop stopColor="#A8E1DF"/>
            <stop offset="1" stopColor="#DDF8C3"/>
          </linearGradient>
        </defs>
      </svg>

      {/* Health cards positioned along the curve */}
      <div className="absolute inset-0">
        <GlassCard 
          className="absolute left-[5%] sm:left-[5%] bottom-1 sm:bottom-0 min-w-[110px] sm:min-w-[140px]"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
            transition: 'opacity 0.6s ease-out 3.2s, transform 0.6s ease-out 3.2s'
          }}
        >
          <div className="font-medium">{cards.spendingLabel}</div>
          <div className="text-[12px] sm:text-[13px] text-night/80 flex items-center gap-1">
            {cards.spendingStatus} {spendingStatusIcon === 'none' ? null : spendingStatusIcon === 'arrow-up' ? <ArrowUpIcon className="text-black" /> : spendingStatusIcon === 'arrow-down' ? <ArrowDownIcon className="text-black" /> : <CheckIcon className="text-black" />}
          </div>
        </GlassCard>

        <GlassCard 
          className="absolute left-[15%] sm:left-[25%] top-[35%] sm:top-[42%] min-w-[110px] sm:min-w-[140px]"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
            transition: 'opacity 0.6s ease-out 3.6s, transform 0.6s ease-out 3.6s'
          }}
        >
          <div className="font-medium">{cards.savingsRateLabel}</div>
          <div className="text-[12px] sm:text-[13px] text-night/80">{cards.savingsRateValue}</div>
        </GlassCard>

        <GlassCard
          className="absolute left-[55%] sm:left-[55%] top-[38%] sm:top-[38%] min-w-[120px] sm:min-w-[170px]"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
            transition: 'opacity 0.6s ease-out 4.0s, transform 0.6s ease-out 4.0s'
          }}
        >
          <div className="font-medium">{cards.liquidityBufferLabel}</div>
          {liquidityBufferContent
            ? liquidityBufferContent
            : <div className="text-[12px] sm:text-[13px] text-night/80">{cards.liquidityBufferValue}</div>
          }
        </GlassCard>

        <GlassCard 
          className="absolute right-[6%] sm:right-[5%] top-[8%] sm:top-[8%] min-w-[120px] sm:min-w-[140px]"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
            transition: 'opacity 0.6s ease-out 4.4s, transform 0.6s ease-out 4.4s'
          }}
        >
          <div className="font-medium">{cards.goalsLabel}</div>
          <div className="text-[12px] sm:text-[13px] text-night/80 flex items-center gap-1">
            {cards.goalsStatus} {goalsStatusIcon === 'check' ? <CheckIcon className="text-black" /> : goalsStatusIcon === 'none' ? null : <FlagIcon className="text-black" />}
          </div>
        </GlassCard>
      </div>
      </div>
    </>
  )
}

export default FinancialPulse

